// Helper modul Laporan SIPORT — dipakai form publik & halaman admin.
// Validasi di sini hanya untuk kenyamanan pengguna; backend tetap memvalidasi ulang.

export const OPTION_FIELDS = [
  ["penyakit_dasar", "Penyakit Dasar"],
  ["jenis_percepatan", "Jenis Percepatan Layanan"],
  ["alasan_percepatan", "Alasan Permintaan Percepatan Layanan"],
  ["ews_score", "Score EWS Pasien"],
  ["kriteria_masalah", "Kriteria Masalah"],
  ["tindak_lanjut", "Tindak Lanjut Percepatan berdasarkan Kondisi Pasien"],
];

export const KATEGORI_LABEL = {
  penyakit_dasar: "Penyakit Dasar",
  jenis_percepatan: "Jenis Percepatan Layanan",
  alasan_percepatan: "Alasan Permintaan Percepatan",
  ews_score: "Score EWS",
  kriteria_masalah: "Kriteria Masalah",
  tindak_lanjut: "Tindak Lanjut",
  alasan_code_blue: "Alasan Aktivasi Code Blue",
};

export const PUBLIC_FORM_PATH = "/lapor-siport";

export function publicFormUrl() {
  return `${window.location.origin}${PUBLIC_FORM_PATH}`;
}

export function todayISO() {
  const d = new Date();
  const wib = new Date(d.getTime() + (d.getTimezoneOffset() + 420) * 60000);
  return `${wib.getFullYear()}-${String(wib.getMonth() + 1).padStart(2, "0")}-${String(wib.getDate()).padStart(2, "0")}`;
}

// Tanggal WIB (UTC+7, tanpa DST) dari epoch ms -> "YYYY-MM-DD"
export function wibDateISO(ms) {
  return new Date(ms + 7 * 3600000).toISOString().slice(0, 10);
}

// Milidetik sampai tengah malam WIB berikutnya
export function msSampaiTengahMalamWIB(ms) {
  const hari = 86400000;
  const wib = ms + 7 * 3600000;
  return hari - (wib % hari);
}

export function monthStartISO() {
  return `${todayISO().slice(0, 8)}01`;
}

export function fmtTanggal(iso) {
  if (!iso) return "—";
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
}

const BULAN = ["Januari", "Februari", "Maret", "April", "Mei", "Juni", "Juli", "Agustus", "September", "Oktober",
  "November", "Desember"];

// "2026-09-27" -> "27 September 2026"
export function fmtTanggalPanjang(iso) {
  if (!iso) return "—";
  const [y, m, d] = iso.split("-");
  return `${Number(d)} ${BULAN[Number(m) - 1]} ${y}`;
}

export function fmtWaktu(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString("id-ID", { dateStyle: "medium", timeStyle: "short" });
}

// "4909270" -> "490-92-70" (diformat bertahap saat mengetik)
export function formatRM(value) {
  const digits = String(value || "").replace(/\D/g, "").slice(0, 7);
  if (digits.length <= 3) return digits;
  if (digits.length <= 5) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`;
}

export function isValidRM(value) {
  return String(value || "").replace(/\D/g, "").length === 7;
}

export function parseJam(s) {
  const m = /^(\d{1,2})[:.](\d{2})/.exec(s || "");
  if (!m) return null;
  const h = +m[1];
  const mi = +m[2];
  if (h > 23 || mi > 59) return null;
  return h * 60 + mi;
}

// Sama dengan backend: tangani lewat tengah malam (23:58 -> 00:03 = 5),
// selisih > 12 jam dianggap jam TMRC sebelum aktivasi (salah input).
export function hitungRespon(jamAktivasi, jamTmrc) {
  const a = parseJam(jamAktivasi);
  const t = parseJam(jamTmrc);
  if (a == null || t == null) return { menit: null, error: null };
  const diff = (((t - a) % 1440) + 1440) % 1440;
  if (diff > 720) return { menit: null, error: "Jam TMRC datang tidak boleh sebelum jam aktivasi" };
  return { menit: diff, error: null };
}

// Sama dengan rapikan_nama_poli() di backend: trim, spasi ganda -> satu, Title Case;
// singkatan huruf besar pendek (THT, KB, IPD) dipertahankan.
export function rapikanNamaPoli(value) {
  const s = String(value || "").replace(/\s+/g, " ").trim();
  if (!s) return "";
  return s
    .split(" ")
    .map((w) =>
      w === w.toUpperCase() && w !== w.toLowerCase() && w.length <= 5
        ? w
        : w.replace(/\p{L}+/gu, (m) => m[0].toUpperCase() + m.slice(1).toLowerCase()),
    )
    .join(" ");
}

export const POLI_MANUAL_MIN = 3;

export const EMPTY_FORM = {
  tanggal_kejadian: "",
  poliklinik_id: "",
  poliklinik_manual: false,
  poliklinik_nama_manual: "",
  nama_dokter_pengisi: "",
  nama_pasien: "",
  usia: "",
  no_rm: "",
  diagnosa: "",
  penyakit_dasar: "",
  jenis_percepatan: "",
  alasan_percepatan: "",
  ews_score: "",
  kriteria_masalah: "",
  tindak_lanjut: "",
  cb_alasan: [],
  cb_lainnya_on: false,
  cb_lainnya: "",
  jam_aktivasi: "",
  jam_tmrc_datang: "",
  akhir_code_blue: "",
};

export const EMPTY_CODE_BLUE = {
  cb_alasan: [],
  cb_lainnya_on: false,
  cb_lainnya: "",
  jam_aktivasi: "",
  jam_tmrc_datang: "",
  akhir_code_blue: "",
};

export function isCodeBlueSelected(form, options) {
  const tl = (options?.tindak_lanjut || []).find((o) => o.id === form.tindak_lanjut);
  return !!tl?.trigger_code_blue;
}

export function validateForm(form, options, hariIni) {
  const e = {};
  const req = (k, msg = "Wajib diisi") => {
    if (form[k] === "" || form[k] == null || (typeof form[k] === "string" && !form[k].trim())) e[k] = msg;
  };
  req("tanggal_kejadian");
  if (form.tanggal_kejadian && form.tanggal_kejadian > (hariIni || todayISO()))
    e.tanggal_kejadian = "Tidak boleh melebihi hari ini";
  if (form.poliklinik_manual) {
    const nama = rapikanNamaPoli(form.poliklinik_nama_manual);
    if (!nama) e.poliklinik_nama = "Tuliskan nama poliklinik";
    else if (nama.length < POLI_MANUAL_MIN) e.poliklinik_nama = `Minimal ${POLI_MANUAL_MIN} karakter`;
  } else if (!form._poliklinik_nama) req("poliklinik_id", "Pilih poliklinik");
  req("nama_dokter_pengisi");
  req("nama_pasien");
  req("usia");
  if (form.usia !== "" && (!Number.isInteger(+form.usia) || +form.usia < 0 || +form.usia > 120))
    e.usia = "Isi angka 0–120";
  req("no_rm");
  if (form.no_rm && !isValidRM(form.no_rm)) e.no_rm = "Harus 7 digit (xxx-xx-xx)";
  req("diagnosa");
  OPTION_FIELDS.forEach(([k]) => req(k, "Pilih salah satu"));
  if (isCodeBlueSelected(form, options)) {
    if (!form.cb_alasan.length && !(form.cb_lainnya_on && form.cb_lainnya.trim()))
      e.cb_alasan = "Pilih minimal satu alasan";
    if (form.cb_lainnya_on && !form.cb_lainnya.trim()) e.cb_lainnya = "Tuliskan alasan lainnya";
    req("jam_aktivasi");
    req("jam_tmrc_datang");
    const r = hitungRespon(form.jam_aktivasi, form.jam_tmrc_datang);
    if (r.error) e.jam_tmrc_datang = r.error;
    req("akhir_code_blue");
  }
  return e;
}

export function toPayload(form, options) {
  const p = {
    tanggal_kejadian: form.tanggal_kejadian,
    poliklinik_id: form.poliklinik_manual ? null : form.poliklinik_id || null,
    poliklinik_manual: !!form.poliklinik_manual,
    poliklinik_nama: form.poliklinik_manual ? rapikanNamaPoli(form.poliklinik_nama_manual) : null,
    nama_dokter_pengisi: form.nama_dokter_pengisi.trim(),
    nama_pasien: form.nama_pasien.trim(),
    usia: form.usia === "" ? null : Number(form.usia),
    no_rm: form.no_rm,
    diagnosa: form.diagnosa.trim(),
    code_blue: null,
  };
  OPTION_FIELDS.forEach(([k]) => (p[k] = form[k] || null));
  if (isCodeBlueSelected(form, options)) {
    p.code_blue = {
      alasan: form.cb_alasan,
      alasan_lainnya: form.cb_lainnya_on ? form.cb_lainnya.trim() : null,
      jam_aktivasi: form.jam_aktivasi,
      jam_tmrc_datang: form.jam_tmrc_datang,
      akhir_code_blue: form.akhir_code_blue.trim(),
    };
  }
  return p;
}

// Laporan tersimpan -> state form (untuk edit admin)
export function reportToForm(r) {
  const cb = r.code_blue || {};
  const f = {
    ...EMPTY_FORM,
    tanggal_kejadian: r.tanggal_kejadian || "",
    poliklinik_id: r.poliklinik_id || "",
    poliklinik_manual: !!r.poliklinik_manual,
    poliklinik_nama_manual: r.poliklinik_manual ? r.poliklinik_nama || "" : "",
    _poliklinik_nama: r.poliklinik_id || r.poliklinik_manual ? "" : r.poliklinik_nama || "",
    nama_dokter_pengisi: r.nama_dokter_pengisi || "",
    nama_pasien: r.nama_pasien || "",
    usia: r.usia ?? "",
    no_rm: r.no_rm || "",
    diagnosa: r.diagnosa || "",
    cb_alasan: (cb.alasan || []).map((a) => a.id),
    cb_lainnya_on: !!cb.alasan_lainnya,
    cb_lainnya: cb.alasan_lainnya || "",
    jam_aktivasi: cb.jam_aktivasi || "",
    jam_tmrc_datang: cb.jam_tmrc_datang || "",
    akhir_code_blue: cb.akhir_code_blue || "",
  };
  OPTION_FIELDS.forEach(([k]) => (f[k] = r[k]?.id || ""));
  return f;
}

export function alasanCodeBlueText(cb) {
  if (!cb) return "";
  const parts = (cb.alasan || []).map((a) => a.label);
  if (cb.alasan_lainnya) parts.push(cb.alasan_lainnya);
  return parts.join(", ");
}

export function EwsDot({ warna, className = "" }) {
  return (
    <span
      className={`inline-block w-3 h-3 rounded-full shrink-0 ring-1 ring-black/10 ${className}`}
      style={{ backgroundColor: warna || "#94A3B8" }}
    />
  );
}

export async function downloadBlob(res, fallbackName) {
  const cd = res.headers?.["content-disposition"] || "";
  const m = /filename="?([^"]+)"?/.exec(cd);
  const url = URL.createObjectURL(res.data);
  const a = document.createElement("a");
  a.href = url;
  a.download = m ? m[1] : fallbackName;
  a.click();
  URL.revokeObjectURL(url);
}
